import { PositionGPS } from '../types';

export interface GeolocationError {
  code: number;
  message: string;
}

export const geolocationService = {
  /**
   * Obtenir la position actuelle de l'utilisateur
   */
  getCurrentPosition: (): Promise<PositionGPS> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject({
          code: 0,
          message: 'La géolocalisation n\'est pas supportée par ce navigateur'
        });
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          let message = 'Erreur de géolocalisation';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              message = 'L\'accès à la géolocalisation a été refusé';
              break;
            case error.POSITION_UNAVAILABLE:
              message = 'Les informations de localisation ne sont pas disponibles';
              break;
            case error.TIMEOUT:
              message = 'Délai d\'attente dépassé pour obtenir la position';
              break;
          }
          reject({
            code: error.code,
            message
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  },

  /**
   * Calculer la distance entre deux points GPS en kilomètres
   */
  calculateDistance: (pos1: PositionGPS, pos2: PositionGPS): number => {
    const R = 6371; // Rayon de la Terre en kilomètres
    const dLat = (pos2.latitude - pos1.latitude) * Math.PI / 180;
    const dLon = (pos2.longitude - pos1.longitude) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(pos1.latitude * Math.PI / 180) * Math.cos(pos2.latitude * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  },

  /**
   * Vérifier si une position est dans un rayon donné
   */
  isWithinRadius: (center: PositionGPS, point: PositionGPS, radiusKm: number): boolean => {
    const distance = geolocationService.calculateDistance(center, point);
    return distance <= radiusKm;
  },

  /**
   * Geocoder une adresse (utilise une API de geocoding gratuite)
   */
  geocodeAddress: async (address: string): Promise<PositionGPS> => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        return {
          latitude: parseFloat(data[0].lat),
          longitude: parseFloat(data[0].lon)
        };
      } else {
        throw new Error('Adresse non trouvée');
      }
    } catch (error) {
      throw new Error('Erreur lors du géocodage de l\'adresse');
    }
  }
}; 